import axios from 'axios';
import { Payment, RefundPaymentRequest } from '../interfaces/payments';
import { StrapiResponse, StrapiMeta } from '../interfaces/commons';
import QueryString from 'qs';
const instance = axios.create();

export interface SuspendedPaymentOrder {
  id: number;
  productType: string;
  profile: {
    id: number;
    name: string;
    surname: string;
    email: string | null;
  };
  payment: {
    id: number;
    method: string;
    status: string;
    createdAt: string;
    amount: number | null;
  };
}

export interface SuspendedPaymentsResponse {
  data: SuspendedPaymentOrder[];
  meta: StrapiMeta;
}

const { REACT_APP_API_URL } = process.env;

const update = async ({
  body,
  token,
  id,
}: {
  body: Partial<Payment>;
  token?: string | null;
  id?: number | string;
}) => {
  const { data } = await instance.put<PaymentResponse>(
    `${REACT_APP_API_URL}/payments/${id}`,
    { data: body },
    {
      headers: { authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const updateAmount = async ({
  amount,
  token,
  id,
}: {
  amount: number;
  token?: string | null;
  id?: number | string;
}) => {
  const { data } = await instance.put<PaymentResponse>(
    `${REACT_APP_API_URL}/payments/${id}/amount`,
    { data: { amount } },
    {
      headers: { authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const refund = async ({
  body,
  token,
  id,
}: {
  body: Partial<RefundPaymentRequest>;
  token?: string | null;
  id?: number | string;
}) => {
  await instance.post(`${REACT_APP_API_URL}/payments/${id}/refund`, body, {
    headers: { authorization: `Bearer ${token}` },
  });
};

export const findOne = async ({
  token,
  id,
  query = {},
}: {
  token?: string | null;
  id?: number | string | null;
  query?: object;
}) => {
  const { data } = await instance.get<StrapiResponse<{ attributes: Payment }>>(
    `${REACT_APP_API_URL}/payments/${id}?${QueryString.stringify(query)}`,
    {
      headers: { authorization: `Bearer ${token}` },
    }
  );
  return data;
};

const find = async ({
  token,
  query = {},
}: {
  token?: string | null;
  id?: number | string | null;
  query?: object;
}) => {
  const { data } = await instance.get<StrapiResponse<{ attributes: Payment }>>(
    `${REACT_APP_API_URL}/payments?${QueryString.stringify(query)}`,
    {
      headers: { authorization: `Bearer ${token}` },
    }
  );
  return data;
};

export const listSuspended = async (
  searchParams: URLSearchParams,
  token: string | null,
): Promise<SuspendedPaymentsResponse> => {
  const page = searchParams.get('page') || '1';
  const pageSize = searchParams.get('pageSize') || '20';

  const dateParam = searchParams.get('date');
  let dateFrom: string | undefined;
  let dateTo: string | undefined;

  if (dateParam) {
    const parsedLabel = QueryString.parse(
      (
        Object.entries(QueryString.parse(dateParam)).map(
          ([, value]) => value,
        )[0] as any
      )?.label,
    );
    dateFrom = parsedLabel.from as string | undefined;
    if (parsedLabel.to) {
      const d = new Date(parsedLabel.to as string);
      d.setDate(d.getDate() + 1);
      dateTo = d.toISOString();
    }
  }

  const query = {
    pagination: { page, pageSize },
    filters: {
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
    },
  };

  const { data } = await instance.get<SuspendedPaymentsResponse>(
    `${REACT_APP_API_URL}/payments/sospesi?${QueryString.stringify(query)}`,
    { headers: { authorization: `Bearer ${token}` } },
  );
  return data;
};

const payments = {
  update,
  updateAmount,
  findOne,
  find,
  refund,
};

export default payments;
